import type { BadgeVariant } from '@/components/ui/badge';
import { DateTime } from 'luxon';

/**
 * The invoice status state machine, extracted from `invoice-status.tsx` so
 * the datawarehouse Avier & Fakturor report can reuse it on DW documents
 * (snake_case, no matrix metas). The branch ORDER is the contract — it
 * mirrors what users see in the invoicing list. Change the two files
 * together: this core and the `getInvoiceStatus` mapper are one machine.
 */
export interface InvoiceStatusInput {
  /** Legacy: invoiceValuesMeta.total_value ?? 0. DW: Σ rows net+vat. */
  totalValue: number;
  closedTime: string | null | undefined;
  /** debt_invoice_counts.unclosed_debt_invoices */
  unclosedDebtInvoices: number | null | undefined;
  /** debt_invoice_counts.kinds['1'] — debt collection invoices sent. */
  sentDebtCount: number | null | undefined;
  /** debt_invoice_counts.kinds['0'] — reminders sent. */
  sentReminderCount: number | null | undefined;
  automaticDebtInvoicingPaused: boolean | null | undefined;
  reminderAmount: number | null | undefined;
  attestedDate: string | null | undefined;
  distributionDate: string | null | undefined;
  dueDate: string | null | undefined;
  /** Legacy: invoiceComputedMeta.payments_sum. DW: invoice.paid_amount. */
  paymentsSum: number | null | undefined;
  invoiceNumber: number | string | null | undefined;
  ocrNumber: string | null | undefined;
}

export interface InvoiceStatusResult {
  text: string;
  badgeVariant: BadgeVariant['variant'];
  additionalInfo?: string;
}

export function getInvoiceStatusCore(
  input: InvoiceStatusInput,
  now: DateTime = DateTime.now()
): InvoiceStatusResult {
  if (input.totalValue < 0) {
    return { text: 'Krediterad', badgeVariant: 'outline' };
  }

  if (input.closedTime) {
    if (input.unclosedDebtInvoices && input.unclosedDebtInvoices > 0) {
      return {
        text: 'Slutbetald (Öppna påminnelser/krav)',
        badgeVariant: 'orange',
      };
    }
    return { text: 'Slutbetald', badgeVariant: 'green' };
  }

  // has actually sent debt
  if (input.sentDebtCount !== undefined && (input.sentDebtCount ?? 0) > 0) {
    return input.automaticDebtInvoicingPaused
      ? { text: 'Kravhantering (Pausad)', badgeVariant: 'light-gray' }
      : { text: 'Kravhantering', badgeVariant: 'purple' };
  }

  // A null dueDate makes fromISO an Invalid DateTime whose comparisons are
  // always false — the legacy code relies on that; keep it explicit here.
  const pastDuePlusOne = input.dueDate
    ? now > DateTime.fromISO(input.dueDate).plus({ days: 1 })
    : false;

  // reminders — has actually sent reminders
  if (
    input.sentReminderCount !== undefined &&
    (input.sentReminderCount ?? 0) > 0
  ) {
    return input.automaticDebtInvoicingPaused
      ? { text: 'Påminnelse skickad (Pausad)', badgeVariant: 'light-gray' }
      : { text: 'Påminnelse skickad', badgeVariant: 'purple' };
  } else if (
    input.reminderAmount &&
    input.reminderAmount > 0 &&
    input.attestedDate &&
    pastDuePlusOne
  ) {
    return input.automaticDebtInvoicingPaused
      ? { text: 'Påminnelser pausade', badgeVariant: 'gray' }
      : { text: 'Påminnelse kommer skickas', badgeVariant: 'purple' };
  }

  // no reminders but overdue
  if (pastDuePlusOne && !input.closedTime && input.attestedDate) {
    return { text: 'Förfallen', badgeVariant: 'destructive' };
  }

  if (input.distributionDate && input.attestedDate && !input.closedTime) {
    if (input.paymentsSum && input.paymentsSum > 0) {
      return { text: 'Skickad, delbetald', badgeVariant: 'cyan' };
    }
    return { text: 'Skickad', badgeVariant: 'blue' };
  }

  if (!input.attestedDate) {
    return { text: 'Ej attesterad', badgeVariant: 'yellow' };
  }

  if (input.invoiceNumber && input.ocrNumber) {
    return { text: 'Attesterad', badgeVariant: 'green' };
  }

  return {
    text: 'Misslyckad attestering',
    badgeVariant: 'destructive',
    additionalInfo:
      'Attesteringen av denna avi/faktura misslyckades. Försök igen.',
  };
}
